import type { ComponentType, ReactNode, UIEventHandler } from "react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { TeamListItem } from "../../types/teams.types";
import { filterTeamsBySearch } from "../../utils/filterTeamsBySearch";

import { AssignTeamOption } from "./AssignTeamOption";

type ComboboxListContentProps = Parameters<typeof Combobox.Content>[0] & {
    onScroll?: UIEventHandler<Element>;
};

const ComboboxListContent = Combobox.Content as ComponentType<ComboboxListContentProps>;

const ASSIGNED_TEAMS_COMBOBOX_WIDTH = "360px";

type AssignTeamsComboboxProps = {
    teams: readonly TeamListItem[];
    selectedTeamIds: number[];
    onToggleTeam: (teamId: number) => void;
    selectedUserIds: readonly number[];
    isOpen: boolean;
    trigger?: ReactNode;
    isDisabled?: boolean;
    dataAutomationPrefix?: string;
    onApply?: () => void;
    isApplyDisabled?: boolean;
    applyDataAutomation?: string;
};

export const AssignTeamsCombobox = ({
    teams,
    selectedTeamIds,
    onToggleTeam,
    selectedUserIds,
    isOpen,
    trigger,
    isDisabled = false,
    dataAutomationPrefix = "assign-users-team",
    onApply,
    isApplyDisabled = false,
    applyDataAutomation = "assign-users-team-save",
}: AssignTeamsComboboxProps) => {
    const { translate } = useTranslate();
    const [searchTerm, setSearchTerm] = useState("");
    const [tooltipDismissSignal, setTooltipDismissSignal] = useState(0);
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const filteredTeams = useMemo(() => filterTeamsBySearch(teams, deferredSearchTerm), [teams, deferredSearchTerm]);
    const selectedTeamIdSet = useMemo(() => new Set(selectedTeamIds), [selectedTeamIds]);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={() => undefined}
            placement="bottom-start"
            width={ASSIGNED_TEAMS_COMBOBOX_WIDTH}
            initialFocus={-1}
            offset={0}
            data-automation={`${dataAutomationPrefix}-combobox`}
        >
            {trigger ?? (
                <Combobox.Trigger>
                    <Flex aria-hidden />
                </Combobox.Trigger>
            )}

            <Combobox.SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={translate("si.management.teams.users.bulk.search_teams")}
                isDisabled={isDisabled}
                data-automation={`${dataAutomationPrefix}-search`}
            />

            <ComboboxListContent
                selectionMode="multiple"
                emptyState={<Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>}
                data-automation={`${dataAutomationPrefix}-list`}
                onScroll={() => setTooltipDismissSignal((signal) => signal + 1)}
            >
                {filteredTeams.map((team) => (
                    <AssignTeamOption
                        key={team.teamId}
                        team={team}
                        isChecked={selectedTeamIdSet.has(team.teamId)}
                        isDisabled={isDisabled}
                        selectedUserIds={selectedUserIds}
                        onToggleTeam={onToggleTeam}
                        tooltipDismissSignal={tooltipDismissSignal}
                        dataAutomationPrefix={dataAutomationPrefix}
                    />
                ))}
            </ComboboxListContent>
            {onApply ? (
                <Combobox.ApplyButton
                    isDisabled={isApplyDisabled}
                    onClick={onApply}
                    data-automation={applyDataAutomation}
                >
                    {translate("si.common.button.save")}
                </Combobox.ApplyButton>
            ) : null}
        </Combobox>
    );
};
